import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Col, Input, Row, Spinner } from "reactstrap";
import { IoClose, IoAddCircleOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import { toast } from "react-toastify";
import DefaultDrawer from "../../../../../components/Drawers/Default";
import DefaultButton from "../../../../../components/Buttons/Default";
import IconButton from "../../../../../components/Buttons/IconButton";
import { del, get, post } from "../../../../../utils/helpers/api";
import { useSettings } from "../../../../../context/settings";
import { formatMoney } from "../../../utils/payrollStatus";

const STYLES = {
  wrap: { display: "flex", flexDirection: "column", height: "100%", background: "#fff" },
  header: { display: "flex", alignItems: "center", gap: 8, padding: "0 16px", height: 52, borderBottom: "1px solid #eaecf0", flexShrink: 0 },
  closeBtn: { background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" },
  title: { fontSize: 14, fontWeight: 700, color: "#1d1d1f" },
  scroll: { flex: 1, overflowY: "auto", padding: 20 },
  employee: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", border: "1px solid #eaecf0", borderRadius: 8, marginBottom: 20 },
  avatar: { width: 42, height: 42, borderRadius: "50%", background: "#eef0ff", color: "#6C63FF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0 },
  section: { fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "#aaa", marginBottom: 10, textTransform: "uppercase" },
  block: { marginTop: 24, paddingTop: 18, borderTop: "1px solid #eee" },
  line: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 0", fontSize: 13, borderBottom: "1px solid #f5f5f5" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0" }
};

const SOURCE_COLORS = {
  benefit: 'light',
  attendance: 'warning',
  overtime: 'info',
  manual: 'primary'
};

const emptyLine = { name: '', type: 'deduction', amount: '', notes: '' };

export default function PayslipDrawer({ payslipId, isOpen, onClose, editable, currency, refreshRun }) {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine] = useState(emptyLine);

  const getPayslip = async () => {
    const data = await get(`/hr/payslips/view/${payslipId}`);
    if (data?.success) {
      setPayslip(data.payslip);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!payslipId) return;
    setLoading(true);
    setPayslip(null);
    setShowAddLine(false);
    setNewLine(emptyLine);
    getPayslip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payslipId]);

  const addLine = async () => {
    setWorking(true);
    const data = await post(`/hr/payslips/lines/store/${payslipId}`, {
      name: newLine.name,
      type: newLine.type,
      amount: Number(newLine.amount) || 0,
      notes: newLine.notes || null
    });
    if (data?.success) {
      await getPayslip();
      refreshRun?.();
      setNewLine(emptyLine);
      setShowAddLine(false);
      toast.success(t('hr.payrollRun.lineAdded'));
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setWorking(false);
  };

  const removeLine = async (lineId) => {
    setWorking(true);
    const data = await del(`/hr/payslips/lines/delete/${payslipId}/${lineId}`);
    if (data?.success) {
      await getPayslip();
      refreshRun?.();
      toast.success(t('hr.payrollRun.lineRemoved'));
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setWorking(false);
  };

  const initials = payslip?.employee_name
    ? payslip.employee_name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("")
    : "—";

  const allowances = payslip?.lines?.filter(line => line.type === 'allowance') || [];
  const deductions = payslip?.lines?.filter(line => line.type === 'deduction') || [];

  const renderLine = (line) => (
    <div key={line.id} style={STYLES.line}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="fw-semibold">{line.name}</span>
        {line.source !== 'benefit' && (
          <Badge color={SOURCE_COLORS[line.source] || 'light'} className="text-uppercase" style={{ fontSize: 9 }}>
            {t(`hr.payrollRun.source.${line.source}`)}
          </Badge>
        )}
        {line.calculation_type === 'percentage' && (
          <span className="text-muted small">{line.value}%</span>
        )}
        {line.notes && <span className="text-muted small">· {line.notes}</span>}
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className={line.type === 'allowance' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
          {line.type === 'allowance' ? '+' : '-'}{formatMoney(line.amount)}
        </span>
        {editable && line.source === 'manual' && (
          <IconButton size="sm" outline color="danger" onClick={() => removeLine(line.id)} disabled={working}>
            <BsTrash3 />
          </IconButton>
        )}
      </div>
    </div>
  );

  return (
    <DefaultDrawer isOpen={isOpen} size={640} toggle={onClose}>
      <div style={STYLES.wrap}>
        <div style={STYLES.header}>
          <button style={STYLES.closeBtn} onClick={onClose}>
            <IoClose size={20} />
          </button>
          <span style={STYLES.title}>{t('hr.payrollRun.payslip')}</span>
          {payslip && <span className="text-muted small">{payslip.reference}</span>}
        </div>

        <div style={STYLES.scroll}>
          {loading || !payslip ? (
            <Spinner color="primary" className="d-block mx-auto my-4" />
          ) : (
            <>
              <div style={STYLES.employee}>
                <div style={STYLES.avatar}>{initials}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{payslip.employee_name}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    {[payslip.job_title, payslip.department].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              </div>

              <div style={STYLES.section}>{t('hr.payrollRun.period')}</div>
              <Row className="mb-3">
                <Col xs="6" className="small text-muted">{t('hr.payrollRun.paidDays')}</Col>
                <Col xs="6" className="small text-end">{payslip.paid_days} / {payslip.period_days}</Col>
                <Col xs="6" className="small text-muted">{t('hr.payrollRun.absentDays')}</Col>
                <Col xs="6" className="small text-end">{payslip.absent_days}</Col>
                <Col xs="6" className="small text-muted">{t('hr.payrollRun.unpaidLeaveDays')}</Col>
                <Col xs="6" className="small text-end">{payslip.unpaid_leave_days}</Col>
                {payslip.payroll_run && (
                  <>
                    <Col xs="6" className="small text-muted">{t('hr.payrollRun.paymentDate')}</Col>
                    <Col xs="6" className="small text-end">
                      {payslip.payroll_run.payment_date ? formatDate(payslip.payroll_run.payment_date) : '—'}
                    </Col>
                  </>
                )}
              </Row>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.basicSalary')}</div>
                <div style={STYLES.line}>
                  <span className="fw-semibold">{t('hr.basicSalary')}</span>
                  <span className="fw-semibold">{formatMoney(payslip.basic_salary)}</span>
                </div>
                {Number(payslip.contract_salary) !== Number(payslip.basic_salary) && (
                  <p className="text-muted small mt-2 mb-0">
                    {t('hr.payrollRun.proratedFrom', {
                      amount: formatMoney(payslip.contract_salary, currency)
                    })}
                  </p>
                )}
              </div>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.allowance')}</div>
                {allowances.length ? allowances.map(renderLine) : (
                  <p className="text-muted small mb-0">{t('hr.payrollRun.noAllowances')}</p>
                )}
              </div>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('hr.deductions')}</div>
                {deductions.length ? deductions.map(renderLine) : (
                  <p className="text-muted small mb-0">{t('hr.payrollRun.noDeductions')}</p>
                )}
              </div>

              {editable && (
                <div style={STYLES.block}>
                  {!showAddLine ? (
                    <DefaultButton size="sm" color="secondary" outline onClick={() => setShowAddLine(true)}>
                      <IoAddCircleOutline className="me-1" />
                      {t('hr.payrollRun.addAdjustment')}
                    </DefaultButton>
                  ) : (
                    <>
                      <div style={STYLES.section}>{t('hr.payrollRun.addAdjustment')}</div>
                      <p className="text-muted small">{t('hr.payrollRun.adjustmentHint')}</p>
                      <Row className="g-2">
                        <Col md="5">
                          <Input
                            bsSize="sm"
                            placeholder={t('common.name')}
                            value={newLine.name}
                            onChange={(e) => setNewLine({ ...newLine, name: e.target.value })}
                          />
                        </Col>
                        <Col md="3">
                          <Input
                            type="select"
                            bsSize="sm"
                            value={newLine.type}
                            onChange={(e) => setNewLine({ ...newLine, type: e.target.value })}
                          >
                            <option value="allowance">{t('hr.allowance')}</option>
                            <option value="deduction">{t('hr.deduction')}</option>
                          </Input>
                        </Col>
                        <Col md="4">
                          <Input
                            type="number"
                            bsSize="sm"
                            min="0"
                            step="0.01"
                            placeholder={t('common.amount')}
                            value={newLine.amount}
                            onChange={(e) => setNewLine({ ...newLine, amount: e.target.value })}
                          />
                        </Col>
                        <Col md="12">
                          <Input
                            bsSize="sm"
                            placeholder={t('common.notes')}
                            value={newLine.notes}
                            onChange={(e) => setNewLine({ ...newLine, notes: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <div className="d-flex gap-2 mt-2">
                        <DefaultButton
                          size="sm"
                          color="primary"
                          onClick={addLine}
                          loading={working}
                          disabled={working || !newLine.name || newLine.amount === ''}
                        >
                          {t('common.save')}
                        </DefaultButton>
                        <DefaultButton size="sm" color="secondary" outline onClick={() => setShowAddLine(false)}>
                          {t('common.cancel')}
                        </DefaultButton>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={STYLES.block}>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('hr.benefits.totalAllowances')}</span>
                  <span className="text-success">+{formatMoney(payslip.total_allowances, currency)}</span>
                </div>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('hr.benefits.grossSalary')}</span>
                  <span>{formatMoney(payslip.gross_salary, currency)}</span>
                </div>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('hr.benefits.totalDeductions')}</span>
                  <span className="text-danger">-{formatMoney(payslip.total_deductions, currency)}</span>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>{t('hr.benefits.netSalary')}</span>
                  <span>{formatMoney(payslip.net_salary, currency)}</span>
                </div>
              </div>

              <div style={STYLES.block}>
                <div style={STYLES.section}>{t('sales.paymentMethod')}</div>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('sales.paymentMethod')}</span>
                  <span>{payslip.payment_method || '—'}</span>
                </div>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('sales.bankName')}</span>
                  <span>{payslip.bank_name || '—'}</span>
                </div>
                <div style={STYLES.totalRow}>
                  <span className="text-muted">{t('sales.accountNumber')}</span>
                  <span>{payslip.bank_account_number || '—'}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DefaultDrawer>
  );
}
